import { useCallback, useMemo, useState } from "react";

import { MagnifyingGlassIcon } from "@phosphor-icons/react";

import FlexItem from "@gx/dls/containers/FlexItem";
import FlexWrapper, { FlexDirection } from "@gx/dls/containers/FlexWrapper";
import HorizontalDivider from "@gx/dls/dividers/HorizontalDivider";
import TextInput from "@gx/dls/inputs/TextInput";
import Widget, { WidgetVariant } from "@gx/dls/widget/Widget";

import BaseToolbar from "@/layouts/components/BaseToolbar";

import EditSourceOverviewActivityFeedItem from "@/pages/sources/components/edit/tabs/overview/EditSourceOverviewActivityFeedItem";
import type { MockActivityItemType } from "@/pages/sources/components/edit/tabs/overview/types";

import { convertProtobufTimestampToDate, formatDateTime, getTimeAgo } from "@/utils/datetimes";

const FullBleedDivider = () => {
  return (
    <FlexWrapper width="calc(100% + 24px)" margin="0 -12px">
      <HorizontalDivider />
    </FlexWrapper>
  );
};

interface EditSourceOverviewActivityFeedProps {
  items: MockActivityItemType[];
}

const EditSourceOverviewActivityFeed = ({ items }: EditSourceOverviewActivityFeedProps) => {
  const [activitySearch, setActivitySearch] = useState("");

  const handleActivitySearchChange = useCallback((value: string) => {
    setActivitySearch(value);
  }, []);

  const filteredItems = useMemo(() => {
    const query = activitySearch.trim().toLowerCase();
    if (!query) return items;

    return items.filter((item) => {
      const date = convertProtobufTimestampToDate(item.createdAt);
      const timestamp = formatDateTime(date.toISOString());
      const timeAgo = getTimeAgo(date);
      const haystack = `${item.title} ${item.detail} ${timestamp} ${timeAgo}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [items, activitySearch]);

  const toolbarLeadingActions = useMemo(() => {
    return [
      <TextInput
        key="search"
        placeholder="Search activity"
        leading={{ icon: MagnifyingGlassIcon }}
        value={activitySearch}
        onChange={handleActivitySearchChange}
        width={220}
      />,
    ];
  }, [activitySearch, handleActivitySearchChange]);

  return (
    <FlexItem grow={1} shrink={1} basis={0} minHeight={0} fillWidth>
      <FlexWrapper direction={FlexDirection.COLUMN} gap={0} fillWidth fillHeight minHeight={0}>
        <Widget fillWidth fillHeight noHover variant={WidgetVariant.BASE}>
          <FlexWrapper direction={FlexDirection.COLUMN} gap={0} fillWidth fillHeight minHeight={0}>
            <FlexWrapper padding="8px 12px" margin="-12px -12px 0 -12px" fillWidth>
              <BaseToolbar leadingActions={toolbarLeadingActions} />
            </FlexWrapper>
            <FullBleedDivider />
            <FlexWrapper
              direction={FlexDirection.COLUMN}
              gap={8}
              padding="12px 0 0 0"
              fillWidth
              fillHeight
              minHeight={0}
              overflow="auto"
            >
              {filteredItems.map((item, index) => (
                <EditSourceOverviewActivityFeedItem
                  key={item.id}
                  item={item}
                  isOpenInitial={index === 0}
                />
              ))}
            </FlexWrapper>
          </FlexWrapper>
        </Widget>
      </FlexWrapper>
    </FlexItem>
  );
};

export default EditSourceOverviewActivityFeed;
